import React, { useEffect, useState } from "react";
import {
  DeviceEventEmitter,
  Image,
  Platform,
  View,
  ScrollView,
} from "react-native";
import { notify } from "@/components/shared/AppToast";
import tw from "twrnc";

import Container from "@/components/shared/AppContainer";
import Text from "@/components/shared/AppText";
import AppActivityIndicator from "../../shared/AppActivityIndicator";
import useWorkspaceAuth from "../../teams/useWorkspaceAuth";
import { useTeamAccess } from "../../teams/useWorkspace";
import { deleteGuestById, fetchGuests } from "../../http/events";
import useRoomGroup from "../../rooms/useRoomGroup";
import {
  isRoom,
  roomLabelOf,
  roomNounOf,
  usesPrimaryAudience,
} from "../../rooms/rooms";
import AppHeader from "../../shared/AppHeader";
import GuestListItem from "../../shared/GuestListItem";
import useSharePress from "../../hooks/useSharePress";
import Button, { ButtonStack } from "@/components/settings/Button";
import Callout from "@/components/settings/Callout";
import { SettingsPage } from "@/components/settings/SettingsPage";
import {
  CONTENT_MAX_WIDTH,
  statusColors,
  text,
} from "@/components/settings/theme";
import analytics from "../../../analytics";
import { AppBackButton } from "../../shared/Buttons";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import { eventLivePageBase, roomNounText } from "../../globalUtils";
import downloadList from "./downloadList";
import { t, tc, tn } from "@/localization";

const arrayToCsv = (headers, data) => {
  const csvRows = [];
  // getting headers.
  const headerValues = headers.map((header) => header.label);
  csvRows.push(headerValues.join(","));
  // Push into array as comma separated values.
  for (const row of data) {
    const rowValues = headers.map((header) => {
      const escaped = ("" + row[header.key]).replace(/"/g, '\\"'); // To replace the unwanted quotes.
      return `"${escaped}"`; // To escape the comma in a address like string.
    });
    csvRows.push(rowValues.join(","));
  }
  return csvRows.join("\n"); // To enter the next rows in the new line '\n'
};

const GuestList = ({ route, navigation }) => {
  const { showActionSheetWithOptions } = useActionSheet();
  const { slug } = route.params;
  const [event, setEvent] = useState({ slug });
  const [loading, setloading] = useState(true);
  // The account the event is in: yours, or the one you're working in (Teams).
  const { broadcaster, custom_domain } = useWorkspaceAuth();
  const team = useTeamAccess();

  const domain = custom_domain || eventLivePageBase;
  const { alias, title } = event;
  const owner = event.broadcaster || broadcaster;

  // A room's guests sign up on its main event (unless the room sells its own
  // tickets), so this list lives there.
  const { primary: roomPrimary, rooms: groupRooms } = useRoomGroup(event);
  const thisRoom = groupRooms.find((r) => r.slug === slug);
  const guestsOnPrimary =
    isRoom(event) &&
    !!roomPrimary &&
    !!thisRoom &&
    usesPrimaryAudience(thisRoom, roomPrimary);
  const url = alias ? `${domain}${alias}` : `${domain}${owner}/${slug}`;

  useEffect(() => {
    const fetchAsync = async () => {
      try {
        const myEvent = await fetchGuests(slug);
        if (!myEvent) {
          throw new Error("Not found");
        }
        setEvent(myEvent);
        setloading(false);
      } catch (err) {
        console.error(err);

        notify.error(
          t("Couldn't load the guest list"),
          t("Check your connection and try again.")
        );
        navigation.navigate("EventList");
      }
    };

    if (broadcaster) {
      fetchAsync();
      // console.log("SUBSCRIBE FOR UPDATES, ", broadcaster, slug)
      // return subscribeToEventUpdates(broadcaster, slug, (updatedEvent) => {
      //   console.log(updatedEvent)
      //   setEvent({ ...event, ...updatedEvent });
      // });
    }
  }, [slug, broadcaster]);

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener("guest-add", ({ guest }) => {
      setEvent({ ...event, guests: [guest, ...event.guests] });
    });
    return () => {
      sub.remove();
    };
  });

  const deleteGuestAction = async ({ id }) => {
    showActionSheetWithOptions(
      {
        options: [t("Delete"), t("Cancel")],
        cancelButtonIndex: 1,
        destructiveButtonIndex: 0,
        title: t("Remove the guest from the list?"),
      },
      async (buttonIndex) => {
        if (buttonIndex === 0) {
          await deleteGuestById(id);
          setEvent({
            ...event,
            guests: event.guests.filter((g) => g.id !== id),
          });
          analytics.track("Delete Guest", {});
        }
      }
    );
  };

  const guests = (event.guests || []).map((guest) => {
    let status = "";
    let color = "";
    switch (true) {
      case !!guest.was_live:
        color = statusColors.joinedLive;
        status = tc("guest status", "Joined Live");
        break;
      case !!guest.was_past:
        color = statusColors.watchedReplay;
        status = tc("guest status", "Watched Replay");
        break;
      case !!guest.was_upcoming:
        color = statusColors.visited;
        status = tc("guest status", "Visited page");
        break;
      case guest.source === "invitation" &&
        !guest.was_upcoming &&
        !guest.was_live &&
        !guest.was_past:
        color = statusColors.invited;
        status = tc("guest status", "Invited");
      default:
        break;
    }

    return { ...guest, status, color };
  });
  // Column names stay English so the file can be imported again (GuestImport).
  const csvHeader = [
    { label: "Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "Status", key: "status" },
    // todo: unsubscribed? bounced? invited? etc..
  ];

  const guestsCopy = arrayToCsv(csvHeader, guests);
  const { sharePress, ShareModal } = useSharePress(title, url);
  const canInvite =
    !!event.event_status &&
    (event.event_status !== "past" || event?.event_privacy === "invite_only");
  const column = [
    tw`w-full self-center px-5`,
    { maxWidth: CONTENT_MAX_WIDTH + 40 },
  ];

  return (
    <>
      <ShareModal />
      <Container>
        <AppHeader
          leftButtonComponent={
            <AppBackButton
              onPress={() => {
                if (navigation.canGoBack()) {
                  navigation.goBack();
                } else {
                  navigation.navigate("EventEdit", route.params);
                }
              }}
            />
          }
          hasLeftButton={true}
        />
        <ScrollView
          style={tw`flex-1`}
          contentContainerStyle={[column, tw`pt-2 pb-6 flex-grow`]}
        >
          <SettingsPage
            title={t("Guest list")}
            help="guests.list"
            description={
              guests.length
                ? tn("{count} guest", "{count} guests", guests.length)
                : undefined
            }
          >
            {loading && <AppActivityIndicator />}
            {!loading && guestsOnPrimary && (
              <Callout
                tone="info"
                action={
                  <Button
                    text={t("Open the guest list")}
                    size="md"
                    fullWidth={false}
                    onPress={() =>
                      navigation.replace("GuestList", {
                        slug: roomPrimary.slug,
                        key: roomPrimary.slug,
                      })
                    }
                  />
                }
              >
                {t(
                  "Guests of every {noun} are on the guest list of {label} (the main link). They sign up once and can watch every {noun}.",
                  {
                    noun: roomNounText(roomNounOf(roomPrimary).toLowerCase()),
                    label: roomLabelOf(roomPrimary),
                  }
                )}
              </Callout>
            )}
            {!loading && !guestsOnPrimary && !guests.length && (
              <View style={tw`items-center justify-center px-6 py-6`}>
                <Image
                  source={require("../../../assets/guestlistEmpty.png")}
                  resizeMode="contain"
                  style={tw`w-[160px] h-[148px] self-center mb-5`}
                />
                <Text style={[text.sectionTitle, tw`text-center mb-1.5`]}>
                  {t("Start inviting guests")}
                </Text>
                <Text style={[text.body, tw`text-center`]}>
                  {t(
                    "Invite guests by email or just share your event link with them."
                  )}
                </Text>
                <Text style={[text.hint, tw`text-center mt-3`]}>
                  {t(
                    "Guests who sign the guestbook or ask for reminders appear here too."
                  )}
                </Text>
              </View>
            )}
            {!loading && !guestsOnPrimary && !!guests.length && (
              <View>
                {guests.map((guest) => (
                  <GuestListItem
                    guest={guest}
                    key={guest.id || guest.email}
                    onDelete={
                      // Teams: removing guests is for admins.
                      team.can("removeGuest", event)
                        ? () => deleteGuestAction(guest)
                        : undefined
                    }
                  />
                ))}
              </View>
            )}
          </SettingsPage>
        </ScrollView>
        {!guestsOnPrimary && (
          <View style={[column, tw`py-4`]}>
            <ButtonStack>
              <Button
                text={t("Share link")}
                variant="primary"
                icon={
                  Platform.OS === "ios"
                    ? "share-outline"
                    : "share-social-outline"
                }
                onPress={sharePress}
              />
              {canInvite && (
                <Button
                  text={t("Invite by email")}
                  icon="mail-outline"
                  onPress={() => {
                    navigation.navigate("GuestAdd", {
                      slug,
                    });
                  }}
                />
              )}
              {!!guests.length && event.event_status && (
                <Button
                  text={t("Download guest list")}
                  icon="download-outline"
                  variant="tertiary"
                  onPress={() => {
                    downloadList(
                      guestsCopy,
                      title
                        ? t("Guests {title}.csv", { title })
                        : t("Guests.csv")
                    );
                  }}
                />
              )}
            </ButtonStack>
          </View>
        )}
      </Container>
    </>
  );
};

export default GuestList;
